import { Icon, useIfUser, useMenu } from '@if/mui'
import { IconButton, Menu, MenuItem, Stack, Tooltip } from '@mui/material'
import {
  CircleCheck,
  EllipsisVertical,
  FolderInput,
  Pencil,
  Trash2,
  TrendingDown,
  TrendingUp,
  WandSparkles,
} from 'lucide-react'
import { useLoanLock } from '@src/providers/LoanLockProvider'
import { ECatchUpLatePaymentMethod, EOperationStatus } from '@src/types/constants'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetOutstandingBalance } from '../LoanCalendar-hooks'
import { ELoanCalendarAction, useLoanCalendarAction } from '../LoanCalendarActionProvider'
import { canChangePaymentStatus } from '../LoanCalendarPaymentStatus/LoanCalendarPaymentStatus-selectors'
import { useLoanCalendarSimulation } from '../LoanCalendarSimulationProvider'
import { CanSpreadOverdueAmountOnDates } from '../LoanSpreadOverdueAmountDialog/LoanSpreadOverdueAmount-selector'

const LoanCalendarTableSecondaryActions = () => {
  const { t } = useTranslation()
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  const { currentSimulation } = useLoanCalendarSimulation()
  const { selectedPaymentIds, dispatchAction } = useLoanCalendarAction()
  const { isReadOnly } = useLoanLock()
  const hasAmountNotReplaced = useGetOutstandingBalance(currentSimulation.simulation) > 0
  const canChangePaymentsStatus = canChangePaymentStatus(selectedPaymentIds, currentSimulation.simulation)
  const [buttonMagicProps, menuMagicProps, handleMagicMenuClose] = useMenu()

  const handleMagicMenuMethodSelect = (method: ECatchUpLatePaymentMethod) => {
    handleMagicMenuClose()

    switch (method) {
      case ECatchUpLatePaymentMethod.CatchUpOnSinglePayment:
        dispatchAction(ELoanCalendarAction.OpenCatchUpAmountOfOverdueForDate)
        break
      case ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments:
        dispatchAction(ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog)
        break
      case ECatchUpLatePaymentMethod.CatchUpOnNextPayments:
        dispatchAction(ELoanCalendarAction.OpenSpreadOverLateAmountDialog)
        break
      default:
        break
    }
  }

  // Payment edits only apply to a selection made of customer payments alone.
  const { canEditPayments, canDeletePayments } = useMemo(() => {
    const selectedOperations = currentSimulation.simulation.operationInformations.filter((opInfo) =>
      selectedPaymentIds.has(opInfo.simulatedOperationId),
    )
    const onlyCustomerPayments =
      selectedOperations.length > 0 && selectedOperations.every((opInfo) => opInfo.isCustomerPaymentOperation())
    const noneCancelled = selectedOperations.every(
      (opInfo) => opInfo.operation.operationStatusId !== EOperationStatus.Cancelled,
    )
    return { canEditPayments: onlyCustomerPayments, canDeletePayments: onlyCustomerPayments && noneCancelled }
  }, [currentSimulation, selectedPaymentIds])

  const paymentDates = useMemo(
    () =>
      currentSimulation.simulation.customerPaymentOperations
        .filter((op) => selectedPaymentIds.has(op.simulatedOperationId))
        .map((p) => p.operation.effectiveDate),
    [currentSimulation, selectedPaymentIds],
  )

  const canSpreadOverPayment = useMemo(
    () => CanSpreadOverdueAmountOnDates(currentSimulation.simulation, paymentDates, canBypassAcpLock),
    [currentSimulation, paymentDates, canBypassAcpLock],
  )
  return (
    <>
      <Stack direction="row" display={{ xs: 'none', md: 'flex' }}>
        <Tooltip title={t('loan.payment.catchUpPayments')}>
          <span>
            <IconButton disabled={isReadOnly || !hasAmountNotReplaced} {...buttonMagicProps}>
              <Icon icon={WandSparkles} />
            </IconButton>
          </span>
        </Tooltip>
        <Menu {...menuMagicProps} onClose={handleMagicMenuClose}>
          <MenuItem onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnSinglePayment)}>
            {t(`enum.ECatchUpLatePaymentMethod.catchUpOnSinglePayment`)}
          </MenuItem>
          <MenuItem onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments)}>
            {t(`enum.ECatchUpLatePaymentMethod.catchUpOnRemainingPayments`)}
          </MenuItem>
          <MenuItem
            disabled={!canSpreadOverPayment}
            onClick={() => handleMagicMenuMethodSelect(ECatchUpLatePaymentMethod.CatchUpOnNextPayments)}
          >
            {t(`enum.ECatchUpLatePaymentMethod.catchUpOnNextPayments`)}
          </MenuItem>
        </Menu>
        <Tooltip title={t('loan.payment.edit')}>
          <span>
            <IconButton
              disabled={isReadOnly || !canEditPayments}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenEditPaymentsDialog)}
            >
              <Icon icon={Pencil} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.moveSelectedPayments.title')}>
          <span>
            <IconButton
              disabled={isReadOnly || !canEditPayments}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenMoveSelectedPaymentsDialog)}
            >
              <Icon icon={FolderInput} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.increase')}>
          <span>
            <IconButton
              disabled={isReadOnly || !canEditPayments}
              onClick={() => {
                dispatchAction(ELoanCalendarAction.OpenIncreasePaymentsDialog)
              }}
            >
              <Icon icon={TrendingUp} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.reduce')}>
          <span>
            <IconButton
              disabled={isReadOnly || !canEditPayments}
              onClick={() => {
                dispatchAction(ELoanCalendarAction.OpenDecreasePaymentsDialog)
              }}
            >
              <Icon icon={TrendingDown} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.editStatus')}>
          <span>
            <IconButton
              disabled={isReadOnly || !canChangePaymentsStatus}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenChangePaymentStatusDialog)}
            >
              <Icon icon={CircleCheck} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.deleteAllSelected')} color="error">
          <span>
            <IconButton
              disabled={isReadOnly || !canDeletePayments}
              onClick={() => dispatchAction(ELoanCalendarAction.OpenDeletePaymentsDialog)}
            >
              <Icon icon={Trash2} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <IconButton sx={{ display: { md: 'none' } }} disabled={isReadOnly}>
        <Icon icon={EllipsisVertical} />
      </IconButton>
    </>
  )
}
export default LoanCalendarTableSecondaryActions
